import React from "react";
import { FaUserCheck, FaShieldAlt, FaEye, FaComments, FaThumbsUp, FaStar } from "react-icons/fa";
import API_BASE from "../config/api";

const PLAN_LABELS = {
    verified_user: { name: "Verified User", icon: "bi-patch-check-fill", color: "#2563eb" },
    term_insurance: { name: "Term Insurance", icon: "bi-shield-fill-check", color: "#0ea5e9" },
    personal_portfolio: { name: "Personal Portfolio", icon: "bi-person-vcard", color: "#8b5cf6" },
    website_link: { name: "Website Link", icon: "bi-globe", color: "#10b981" },
    sms_panel: { name: "SMS Panel", icon: "bi-chat-dots-fill", color: "#f59e0b" },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");

export default function Dashboard({ listinguid, businessName, onSwitch }) {
    const userData = JSON.parse(localStorage.getItem("user"));
    const fullName = userData?.fullname || "User";
    const [weather, setWeather] = React.useState({ temp: null, city: "", country: "" });

    React.useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(async (pos) => {
                const { latitude, longitude } = pos.coords;
                const weatherRes = await fetch(
                    `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true`
                );
                const weatherData = await weatherRes.json();
                const geoRes = await fetch(
                    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
                );
                const geoData = await geoRes.json();
                setWeather({
                    temp: weatherData?.current_weather?.temperature ?? 0,
                    city: geoData?.city || "Unknown",
                    country: geoData?.countryName || ""
                });
            });
        }
    }, []);

    const [stats, setStats] = React.useState({ verified: 0, insurance: 0, pageview: 0, comments: 0, likes: 0, rating: 0 });

    React.useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return;
        const url = `${API_BASE}/api/get_dashboard_data.php?personaluid=${user.personaluid}` +
            (listinguid ? `&listinguid=${encodeURIComponent(listinguid)}` : "");
        fetch(url)
            .then(res => res.json())
            .then(json => { if (json.success) setStats(json.data); });
    }, [listinguid]);

    // Active plans purchased by THIS business
    const [plans, setPlans] = React.useState({ packages: [], promotion: null, owned: [] });

    React.useEffect(() => {
        if (!listinguid) { setPlans({ packages: [], promotion: null, owned: [] }); return; }
        fetch(`${API_BASE}/api/get_listing_upgrades.php?listinguid=${encodeURIComponent(listinguid)}`)
            .then(res => res.json())
            .then(json => {
                if (json.success) setPlans({ packages: json.packages || [], promotion: json.promotion || null, owned: json.owned || [] });
            })
            .catch(() => { });
    }, [listinguid]);


    return (
        <div className="container-fluid py-4" style={{ background: "#f7f9fc", minHeight: "100vh" }}>

            {/* Top Welcome Banner */}
            <div className="rounded p-3 mb-3 shadow-sm" style={{ background: "linear-gradient(135deg, #e8f0ff, #fefefe)", borderLeft: "6px solid #4a90e2" }}>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                        <h5 className="fw-bold text-dark mb-1">Welcome, {fullName}!</h5>
                        <p className="text-secondary mb-0">
                            {businessName
                                ? <>Managing <span className="fw-semibold text-primary">{businessName}</span></>
                                : "Your dashboard for viewing and updating your business insights."}
                        </p>
                    </div>
                    {onSwitch && (
                        <button className="btn btn-sm btn-outline-primary" onClick={onSwitch}>
                            <i className="bi bi-arrow-left-right me-1"></i> Switch Business
                        </button>
                    )}
                </div>
            </div>

            <div className="row">
                {/* Left – Image + Weather */}
                <div className="col-md-6 mb-4">
                    <div className="card shadow-sm border-0" style={{ borderRadius: 20 }}>
                        <img src="logo.png" className="img-fluid p-2" alt="people" />
                        <div className="p-3 d-flex align-items-center">
                            <div>
                                <h2 className="fw-bold text-primary mb-0">
                                    <i className="bi bi-sun-fill me-2"></i>
                                    {weather.temp !== null ? `${weather.temp}°C` : "--"}
                                </h2>
                            </div>
                            <div className="ms-3">
                                <h5 className="fw-semibold mb-0 text-dark">{weather.city || "City"}</h5>
                                <p className="mb-0 text-muted">{weather.country || "Country"}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right — Stats */}
                <div className="col-md-6">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <div className="card shadow-sm border-0 p-2" style={{ background: "#e9f4ff", borderRadius: 16 }}>
                                <FaUserCheck size={26} className="text-primary mb-2" />
                                <h6 className="text-secondary mb-1">Verified Listing</h6>
                                <h4 className="fw-bold text-dark">{stats.verified ? "Yes" : "No"}</h4>
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="card shadow-sm border-0 p-2" style={{ background: "#eaf7ff", borderRadius: 16 }}>
                                <FaShieldAlt size={26} className="text-info mb-2" />
                                <h6 className="text-secondary mb-1">Verified Insurance</h6>
                                <h4 className="fw-bold text-dark">{stats.insurance ? "Yes" : "No"}</h4>
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="card shadow-sm border-0 p-2" style={{ background: "#e6fff3", borderRadius: 16 }}>
                                <FaEye size={26} className="text-success mb-2" />
                                <h6 className="text-secondary mb-1">Page Viewed</h6>
                                <h4 className="fw-bold text-dark">{stats.pageview}</h4>
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="card shadow-sm border-0 p-2" style={{ background: "#fff5e5", borderRadius: 16 }}>
                                <FaComments size={26} className="text-warning mb-2" />
                                <h6 className="text-secondary mb-1">Comments</h6>
                                <h4 className="fw-bold text-dark">{stats.comments}</h4>
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="card shadow-sm border-0 p-2" style={{ background: "#e8f3ff", borderRadius: 16 }}>
                                <FaThumbsUp size={26} className="text-primary mb-2" />
                                <h6 className="text-secondary mb-1">Likes</h6>
                                <h4 className="fw-bold text-dark">{stats.likes}</h4>
                            </div>
                        </div>
                        <div className="col-md-6 mb-3">
                            <div className="card shadow-sm border-0 p-2" style={{ background: "#ffe8e8", borderRadius: 16 }}>
                                <FaStar size={26} className="text-danger mb-2" />
                                <h6 className="text-secondary mb-1">Star Rating</h6>
                                <h4 className="fw-bold text-dark">{stats.rating}</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ACTIVE PLANS FOR THIS BUSINESS */}
            <div className="row mt-2">
                <div className="col-12">
                    <div className="card shadow-sm border-0 p-3" style={{ borderRadius: 16 }}>
                        <h6 className="fw-bold mb-3">
                            <i className="bi bi-box-seam me-2 text-primary"></i>
                            Active Plans{businessName ? ` — ${businessName}` : ""}
                        </h6>

                        {plans.packages.length === 0 && !plans.promotion && (plans.owned || []).filter(o => !o.tracked).length === 0 ? (
                            <div className="text-muted">No active plans for this business yet.</div>
                        ) : (
                            <div className="row g-3">
                                {plans.packages.map((p) => {
                                    const meta = PLAN_LABELS[p.key] || { name: p.key, icon: "bi-star-fill", color: "#2563eb" };
                                    return (
                                        <div className="col-md-4" key={p.key}>
                                            <div className="plan-card">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: "1.2rem" }}></i>
                                                    <span className="fw-semibold">{meta.name}</span>
                                                    <span className="badge bg-success ms-auto">Active</span>
                                                </div>
                                                <small className="text-muted">
                                                    {p.end === "Lifetime" ? "Lifetime" : `${fmtDate(p.start)} → ${fmtDate(p.end)}`}
                                                </small>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Generic packages (not one of the 5 tracked features) */}
                                {(plans.owned || []).filter(o => !o.tracked).map((o) => (
                                    <div className="col-md-4" key={`owned-${o.id}`}>
                                        <div className="plan-card">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <i className="bi bi-box-seam" style={{ color: "#2563eb", fontSize: "1.2rem" }}></i>
                                                <span className="fw-semibold">{o.name}</span>
                                                <span className="badge bg-success ms-auto">Active</span>
                                            </div>
                                            <small className="text-muted">
                                                {o.end === "Lifetime" ? "Lifetime" : `Valid till ${fmtDate(o.end)}`}
                                            </small>
                                        </div>
                                    </div>
                                ))}

                                {plans.promotion && (

                                    <div className="col-md-4">
                                        <div className="plan-card">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <i className="bi bi-megaphone-fill" style={{ color: "#e23744", fontSize: "1.2rem" }}></i>
                                                <span className="fw-semibold">Promotion</span>
                                                <span className={`badge ms-auto ${plans.promotion.status === "active" ? "bg-success" : plans.promotion.status === "upcoming" ? "bg-info" : "bg-secondary"}`}>
                                                    {plans.promotion.status}
                                                </span>
                                            </div>
                                            <small className="text-muted">
                                                {plans.promotion.days} days · {fmtDate(plans.promotion.start)} → {fmtDate(plans.promotion.end)}
                                            </small>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <style>{`
                            .plan-card { border: 1px solid #e9eef5; border-radius: 12px; padding: 12px 14px; background: #fbfdff; height: 100%; }
                        `}</style>
                    </div>
                </div>
            </div>

        </div>
    );
}
